import classNames from 'classnames';
import {Formik, FormikHelpers, FormikProps} from 'formik';
import {Fragment, PureComponent, useCallback, useEffect} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';

import {passwordApi} from 'api';
import {Button, InputFormik, Loader, notifier, NotRobotCaptcha, UnauthorizedLayoutContent} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {UnauthorizedLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {apiUtils} from 'utils';

const PAGE_KEYS = TRANSLATION_KEYS.FORGOT_PASSWORD_PAGE;

type FormFields = {
    email: string;
    captchaToken: string;
};

type FormTemplateProps = {
    isLoading: boolean;
    onChange: (emailInput: string) => void;
} & WithTranslation & FormikProps<FormFields>;

const initialValues: FormFields = {
    email: '',
    captchaToken: null
};

const FormTemplate: React.FC<FormTemplateProps> = ({values, isLoading, setFieldValue, onChange, handleSubmit, t}: FormTemplateProps) => {
    const setCaptchaToken = useCallback((value: string) => {
        setFieldValue('captchaToken', value);
    }, [setFieldValue]);

    const onCaptchaExpired = useCallback(() => {
        setCaptchaToken(null);
    }, [setCaptchaToken]);

    useEffect(() => {
        onChange(values.email);
    }, [values, onChange]);

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}
        >
            <InputFormik
                name='email'
                autoFocus
                required
                autoComplete='email'
                label={t(PAGE_KEYS.FORM.FORM_CONTROL.EMAIL)}
            />

            {!isLoading &&
                <div className='tw-flex tw-justify-center tw-mt-10'>
                    <NotRobotCaptcha
                        onChange={setCaptchaToken}
                        onExpired={onCaptchaExpired}
                    />
                </div>}

            <div className='tw-mt-12'>
                <Button
                    width='full'
                    height='full'
                    type='submit'
                    color='primary'
                    disabled={!values.email || !values.captchaToken}
                >
                    {t(PAGE_KEYS.FORM.BUTTON.RESET_PASSWORD)}
                </Button>
            </div>
        </form>
    );
};

const WrappedFormTemplate = withTranslation()(FormTemplate);

type State = {
    emailInput: string;
    emailSent: boolean;
    isLoading: boolean;
};

const initialState: State = Object.freeze({
    emailInput: '',
    emailSent: false,
    isLoading: false
});

type Props = {
    history: any;
} & WithTranslation;

class ForgotPasswordPage extends PureComponent<Props, State> {

    readonly state = initialState;

    handleSubmit = (values: FormFields, formikHelpers: FormikHelpers<FormFields>) => {
        this.setState({isLoading: true});
        passwordApi.forgotPassword(values).then(() => {
            this.setState({emailSent: true, isLoading: false});
        }, (error) => {
            this.setState({isLoading: false});
            const errors = apiUtils.getResponseErrors(error);
            const validationErrors = apiUtils.getValidationErrors(error);

            if (validationErrors) {
                formikHelpers.setFieldError('email', validationErrors);
            }

            if (errors) {
                notifier.error({details: errors});
            }
        });
    };

    onChange = (emailInput: string) => {
        this.setState({emailInput});
    };

    render() {
        const {onChange, handleSubmit} = this;
        const {t} = this.props;
        const {emailSent, emailInput, isLoading} = this.state;

        return (
            <UnauthorizedLayout icon={emailSent ? 'email' : 'question'}>
                <UnauthorizedLayoutContent
                    title={t(PAGE_KEYS.TITLE)}
                    link={{
                        path: ROUTE_PATH.PUBLIC.LOGIN,
                        name: t(PAGE_KEYS.HAVE_PASSWORD_LINK_TEXT),
                        description: t(PAGE_KEYS.HAVE_PASSWORD_TEXT),
                        descriptionPage: t(PAGE_KEYS.HAVE_PASSWORD_TEXT_PAGE)
                    }}
                >
                    {isLoading && <div className='tw-relative tw-w-10 tw-h-10 tw-mx-auto'><Loader /></div>}

                    {!isLoading && emailSent &&
                        <Fragment>
                            <div className='tw-pb-4 tw-border-b-1'>
                                <p>
                                    {t(PAGE_KEYS.SUCCESS_MSG.LINK_SENT_TO)}
                                    <b>{emailInput}</b>
                                </p>
                                <p>{t(PAGE_KEYS.SUCCESS_MSG.FOLLOW_INSTRUCTION)}</p>
                            </div>
                            <div className='tw-border tw-border-default' />
                        </Fragment>}

                    <div className={classNames({
                        'tw-hidden': isLoading || emailSent
                    })}
                    >
                        <div className='tw-mb-4'>
                            {t(PAGE_KEYS.DESCRIPTION)}
                        </div>

                        <Formik
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                        >
                            {(props) => (
                                <WrappedFormTemplate
                                    {...props}
                                    isLoading={isLoading}
                                    onChange={onChange}
                                />
                            )}
                        </Formik>
                    </div>
                </UnauthorizedLayoutContent>
            </UnauthorizedLayout>
        );
    }
}

export default withTranslation()(ForgotPasswordPage);
